/**
 * GovernanceDocumentDetail - Document Lens Analysis Detail Page
 * ============================================================================
 * DC-1042: Page for viewing and running lens analysis on a governance document
 *
 * Features:
 * - Document header with metadata
 * - LensSelector for choosing which lenses to run
 * - Grid of LensScoreCards showing results
 * - Tabs: Tier 1 Results | Deep Analysis
 * - Export button
 *
 * Route: /settings/governance/documents/:documentId
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useGovernanceLens,
  LensSelector,
  LensScoreCard,
  DeepAnalysisResultsPanel,
  ExportLensAnalysisReport,
  type GovernanceLensId,
  type RegulationSchemaId,
  type LensResult,
  LENS_CONFIG,
  ALL_LENS_IDS,
  SEVERITY_CONFIG,
  sortFindingsBySeverity,
} from '../../components/lenses/governance';
// DC-1647: group-transcript speaker results must be reachable from this detail
// route (previously only via the Owner-column dot in the documents table).
import { SpeakerMappingPanel } from '../../components/triangulation/SpeakerMappingPanel';
import { GroupSessionAnalysisSection } from '../../components/triangulation/GroupSessionAnalysisSection';
import './GovernanceDocumentDetail.css';

// ============================================================================
// Types
// ============================================================================

interface DocumentInfo {
  document_id: string;
  document_name: string;
  document_type: string;
  category: string;
  version: string | null;
  uploaded_at: string;
  analyzed_at: string | null;
}

// ============================================================================
// Main Component
// ============================================================================

export const GovernanceDocumentDetail: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();

  // Document state
  const [document, setDocument] = useState<DocumentInfo | null>(null);
  const [documentLoading, setDocumentLoading] = useState(true);
  const [documentError, setDocumentError] = useState<string | null>(null);

  // Lens selection state
  const [selectedLenses, setSelectedLenses] = useState<GovernanceLensId[]>([...ALL_LENS_IDS]);

  // Tab state
  const [activeTab, setActiveTab] = useState<'tier1' | 'deep'>('tier1');

  // Deep analysis state
  const [selectedRegulation, setSelectedRegulation] = useState<RegulationSchemaId | null>(null);

  // Use governance lens hook
  const {
    availableLenses,
    availableRegulations,
    tier1Result,
    tier1Loading: _tier1Loading,
    tier1Running,
    deepAnalysisResult,
    deepAnalysisLoading,
    deepAnalysisRunning,
    error: hookError,
    fetchTier1Results: _fetchTier1Results,
    runTier1Analysis,
    fetchDeepAnalysis,
    runDeepAnalysis,
    clearError,
  } = useGovernanceLens({ documentId });

  // Fetch document info
  useEffect(() => {
    async function fetchDocument() {
      if (!documentId) return;

      try {
        setDocumentLoading(true);
        setDocumentError(null);

        const response = await fetch(`/api/organization/documents/${documentId}`);
        if (!response.ok) {
          throw new Error('Failed to load document');
        }

        const data = await response.json();
        if (data.success && data.document) {
          setDocument(data.document);
        } else {
          throw new Error(data.error || 'Document not found');
        }
      } catch (err) {
        console.error('[GovernanceDocumentDetail] Error:', err);
        setDocumentError(err instanceof Error ? err.message : 'Failed to load document');
      } finally {
        setDocumentLoading(false);
      }
    }

    fetchDocument();
  }, [documentId]);

  // Fetch deep analysis when regulation changes
  useEffect(() => {
    if (documentId && selectedRegulation) {
      fetchDeepAnalysis(documentId, selectedRegulation);
    }
  }, [documentId, selectedRegulation, fetchDeepAnalysis]);

  // Handle running Tier 1 analysis
  const handleRunTier1 = useCallback(async () => {
    if (!documentId) return;
    await runTier1Analysis(documentId, selectedLenses);
  }, [documentId, selectedLenses, runTier1Analysis]);

  // Handle running deep analysis
  const handleRunDeep = useCallback(
    async (regulationId: RegulationSchemaId) => {
      if (!documentId) return;
      await runDeepAnalysis(documentId, regulationId);
    },
    [documentId, runDeepAnalysis]
  );

  // Get lens result for a specific lens
  const getLensResult = useCallback(
    (lensId: GovernanceLensId): LensResult | null => {
      if (!tier1Result?.lens_results) return null;
      return tier1Result.lens_results[lensId] || null;
    },
    [tier1Result]
  );

  // Build selected lens cards
  const selectedLensCards = useMemo(() => {
    return selectedLenses.map(lensId => ({
      lensId,
      result: getLensResult(lensId),
    }));
  }, [selectedLenses, getLensResult]);

  // Get findings grouped by severity
  const groupedFindings = useMemo(() => {
    if (!tier1Result?.all_findings) return null;
    const sorted = sortFindingsBySeverity(tier1Result.all_findings);
    return {
      critical: sorted.filter(f => f.severity === 'critical'),
      major: sorted.filter(f => f.severity === 'major'),
      minor: sorted.filter(f => f.severity === 'minor'),
      observation: sorted.filter(f => f.severity === 'observation'),
    };
  }, [tier1Result]);

  // Loading state
  if (documentLoading) {
    return (
      <div className="gdd-page gdd-page--loading">
        <div className="gdd-loading">
          <div className="gdd-loading__spinner" />
          <p>Loading document...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (documentError || !document) {
    return (
      <div className="gdd-page gdd-page--error">
        <div className="gdd-error">
          <h2>Error Loading Document</h2>
          <p>{documentError || 'Document not found'}</p>
          <button onClick={() => navigate('/settings/governance/documents')}>
            Back to Documents
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="gdd-page">
      {/* Header */}
      <div className="gdd-header">
        <button
          className="gdd-header__back"
          onClick={() => navigate('/settings/governance/documents')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="gdd-header__info">
          <h1 className="gdd-header__title">{document.document_name}</h1>
          <div className="gdd-header__meta">
            <span className="gdd-header__meta-item">
              Category: <strong>{document.category}</strong>
            </span>
            <span className="gdd-header__meta-item">
              Type: <strong>{document.document_type}</strong>
            </span>
            {document.version && (
              <span className="gdd-header__meta-item">
                Version: <strong>{document.version}</strong>
              </span>
            )}
            <span className="gdd-header__meta-item">
              Uploaded: {new Date(document.uploaded_at).toLocaleDateString()}
            </span>
          </div>
        </div>
        <div className="gdd-header__actions">
          <ExportLensAnalysisReport
            documentId={documentId || ''}
            regulationId={selectedRegulation}
            disabled={!deepAnalysisResult}
          />
        </div>
      </div>

      {/* DC-1647: group-transcript speaker results, reachable directly from the
          document detail route. Both components self-gate on category — no-op
          for any non-group_transcript document. */}
      <GroupSessionAnalysisSection
        documentId={documentId || ''}
        documentName={document.document_name}
        category={document.category}
      />
      <SpeakerMappingPanel documentId={documentId || ''} category={document.category} />

      {/* Error display */}
      {hookError && (
        <div className="gdd-alert gdd-alert--error">
          <span className="gdd-alert__icon">⚠</span>
          <span className="gdd-alert__message">{hookError.message}</span>
          <button onClick={clearError} className="gdd-alert__dismiss">
            ×
          </button>
        </div>
      )}

      {/* Lens Selector Section */}
      <div className="gdd-section">
        <LensSelector
          availableLenses={availableLenses}
          selectedLenses={selectedLenses}
          onSelectionChange={setSelectedLenses}
          disabled={tier1Running}
        />
        <button
          className="gdd-analyze-btn"
          onClick={handleRunTier1}
          disabled={tier1Running || selectedLenses.length === 0}
        >
          {tier1Running ? (
            <>
              <span className="gdd-analyze-btn__spinner" />
              Analyzing...
            </>
          ) : (
            `Analyze Selected Lenses (${selectedLenses.length})`
          )}
        </button>
      </div>

      {/* Lens Score Cards Grid */}
      <div className="gdd-section">
        <h2 className="gdd-section__title">Lens Scores</h2>
        <div className="gdd-lens-grid">
          {selectedLensCards.map(({ lensId, result }) => (
            <LensScoreCard
              key={lensId}
              lensId={lensId}
              result={result}
              loading={tier1Running}
              onClick={() => {
                setActiveTab('tier1');
                // Scroll to findings if available
              }}
            />
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="gdd-tabs">
        <button
          className={`gdd-tab ${activeTab === 'tier1' ? 'gdd-tab--active' : ''}`}
          onClick={() => setActiveTab('tier1')}
        >
          Tier 1 Results
          {tier1Result && (
            <span className="gdd-tab__badge">{tier1Result.all_findings.length}</span>
          )}
        </button>
        <button
          className={`gdd-tab ${activeTab === 'deep' ? 'gdd-tab--active' : ''}`}
          onClick={() => setActiveTab('deep')}
        >
          Deep Analysis
          {deepAnalysisResult && (
            <span className="gdd-tab__badge">
              {Math.round(deepAnalysisResult.compliance_score)}%
            </span>
          )}
        </button>
      </div>

      {/* Tab Content */}
      <div className="gdd-tab-content">
        {activeTab === 'tier1' && (
          <div className="gdd-tier1">
            {!tier1Result ? (
              <div className="gdd-tier1__empty">
                <p>No Tier 1 analysis results yet.</p>
                <p>Select lenses above and click "Analyze" to run the analysis.</p>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="gdd-tier1__summary">
                  <div className="gdd-tier1__score">
                    <span className="gdd-tier1__score-value">
                      {Math.round(tier1Result.overall_score)}
                    </span>
                    <span className="gdd-tier1__score-label">Overall Score</span>
                  </div>
                  <div className="gdd-tier1__stats">
                    <div className="gdd-tier1__stat gdd-tier1__stat--critical">
                      <span className="gdd-tier1__stat-value">
                        {tier1Result.findings_summary.critical}
                      </span>
                      <span className="gdd-tier1__stat-label">Critical</span>
                    </div>
                    <div className="gdd-tier1__stat gdd-tier1__stat--major">
                      <span className="gdd-tier1__stat-value">
                        {tier1Result.findings_summary.major}
                      </span>
                      <span className="gdd-tier1__stat-label">Major</span>
                    </div>
                    <div className="gdd-tier1__stat gdd-tier1__stat--minor">
                      <span className="gdd-tier1__stat-value">
                        {tier1Result.findings_summary.minor}
                      </span>
                      <span className="gdd-tier1__stat-label">Minor</span>
                    </div>
                    <div className="gdd-tier1__stat gdd-tier1__stat--observation">
                      <span className="gdd-tier1__stat-value">
                        {tier1Result.findings_summary.observation}
                      </span>
                      <span className="gdd-tier1__stat-label">Obs</span>
                    </div>
                  </div>
                </div>

                {/* Findings by severity */}
                {groupedFindings && (
                  <div className="gdd-tier1__findings">
                    {(['critical', 'major', 'minor', 'observation'] as const).map(severity => {
                      const findings = groupedFindings[severity];
                      if (findings.length === 0) return null;
                      const config = SEVERITY_CONFIG[severity];

                      return (
                        <div key={severity} className="gdd-findings-group">
                          <h3
                            className="gdd-findings-group__title"
                            style={{ color: config.color }}
                          >
                            {config.icon} {config.label} ({findings.length})
                          </h3>
                          <div className="gdd-findings-group__list">
                            {findings.map(finding => (
                              <div
                                key={finding.id}
                                className={`gdd-finding gdd-finding--${severity}`}
                                style={{ borderLeftColor: config.color }}
                              >
                                <div className="gdd-finding__header">
                                  <span className="gdd-finding__lens">
                                    {LENS_CONFIG[finding.lens_id].shortName}
                                  </span>
                                  <span className="gdd-finding__title">{finding.title}</span>
                                </div>
                                <p className="gdd-finding__description">{finding.description}</p>
                                {finding.suggestion && (
                                  <p className="gdd-finding__suggestion">
                                    💡 {finding.suggestion}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Detected Regulations */}
                {tier1Result.detected_regulations.length > 0 && (
                  <div className="gdd-tier1__detected">
                    <h3>Detected Regulations</h3>
                    <p>
                      Based on document content, the following regulations may be relevant for
                      deep analysis:
                    </p>
                    <div className="gdd-tier1__detected-list">
                      {tier1Result.detected_regulations.map(regId => (
                        <button
                          key={regId}
                          className="gdd-tier1__detected-btn"
                          onClick={() => {
                            setSelectedRegulation(regId);
                            setActiveTab('deep');
                          }}
                        >
                          {regId}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Analysis timestamp */}
                <div className="gdd-tier1__footer">
                  <span>
                    Analyzed: {new Date(tier1Result.analyzed_at).toLocaleString()}
                  </span>
                  <span>Version: {tier1Result.analysis_version}</span>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'deep' && (
          <DeepAnalysisResultsPanel
            documentId={documentId || ''}
            result={deepAnalysisResult}
            availableRegulations={availableRegulations}
            selectedRegulation={selectedRegulation}
            onRegulationChange={setSelectedRegulation}
            onRunAnalysis={handleRunDeep}
            loading={deepAnalysisLoading}
            isRunning={deepAnalysisRunning}
            error={hookError?.message}
          />
        )}
      </div>
    </div>
  );
};

export default GovernanceDocumentDetail;
